import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { acknowledgeSessionExpiredDismissed } from '@/shared/lib/httpClient'
import { onSessionExpired, sessionExpiredReasonFromEvent, type SessionExpiredReason } from '@/shared/lib/sessionExpiry'

/**
 * Modale de session expirée (RV-32). Écoute l'événement émis par `httpClient` sur
 * un 401 mid-session **confirmé** (revérifié via `probeAuthSession`), prend la main
 * (`preventDefault`) pour afficher un AlertDialog (focus trap Radix) au lieu d'une
 * redirection brute. Contrairement à `UrgentModal`, une action secondaire permet de
 * fermer et continuer à naviguer sur la page en cours (faux positif possible malgré
 * la revérification) - seule une future action authentifiée réellement rejetée
 * redéclenchera le flux.
 */
export function SessionExpiredModal() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<SessionExpiredReason>('session')

  useEffect(
    () =>
      onSessionExpired((event) => {
        event.preventDefault()
        setReason(sessionExpiredReasonFromEvent(event))
        setOpen(true)
      }),
    [],
  )

  const reconnect = () => {
    window.location.assign('/auth/login?reason=auth_required')
  }

  // Covers every close path (Cancel button, Escape, outside click) so a dismissed false
  // positive does not permanently block a later, genuinely-confirmed 401 (see
  // `acknowledgeSessionExpiredDismissed`'s doc comment in httpClient.ts).
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      acknowledgeSessionExpiredDismissed()
    }
  }

  const copyKey = reason === 'csrf' ? 'ui.session_csrf_expired' : 'ui.session_expired'

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="size-5 shrink-0 text-[var(--v2-warn)]" aria-hidden />
            <AlertDialogTitle>{t(`${copyKey}.title`)}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>{t(`${copyKey}.message`)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t(`${copyKey}.dismiss`)}</AlertDialogCancel>
          <AlertDialogAction onClick={reconnect}>{t(`${copyKey}.reconnect`)}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
